// RouteContent — the loading + error shell that wraps the page content
// INSIDE PageLayout's chrome (TopNav + Sidebar stay mounted).
//
// THE PROBLEM (Mylah's 2026-07-01 walkthrough): every page is lazy
// (route-level code-splitting) and the only Suspense boundary lived
// ABOVE the router in App.tsx, while the nav chrome renders inside each
// route. So navigating to a not-yet-downloaded route unmounted the
// TopNav + Sidebar and showed the full-screen boot skeleton. If that
// chunk load stalled (slow connection, D1 overload window, a blocked
// reload), there was NOTHING left on screen to click — "it just keeps
// loading and I couldn't go back even if I clicked the other things" —
// and the only escape was a hard browser refresh.
//
// THE FIX — two boundaries that PageLayout mounts around its content:
//
//   * RouteLoadingFallback — content-area skeleton while a page's chunk
//     loads. The chrome stays interactive the whole time, so a stuck
//     page can always be escaped via the sidebar/top nav. After
//     STALL_NOTICE_MS it names the problem in plain words and offers a
//     reload, instead of shimmering forever.
//
//   * RouteErrorBoundary — the app previously had NO error boundary
//     anywhere, so a page that threw during render (e.g. a chunk 404
//     after lazy-with-reload's one-time reload guard) unmounted the
//     entire React tree → blank white screen. Now the failure renders
//     as an in-content card with "Try again", and the boundary resets
//     itself when the user navigates to a different path.
//
// DECISION: the outer App.tsx Suspense (AppBootSkeleton) is kept — it
// still covers the public routes (sign-in, accept-invite, intake) that
// render without PageLayout, and the very first session boot. Only
// authenticated page content moves inside these boundaries.

import { Component, useEffect, useState, type ReactNode } from "react";
import * as Sentry from "@sentry/react";

import { isChunkLoadError } from "../lib/lazy-with-reload";
import { Skeleton } from "../ui";

// Long enough that a slow-but-working connection never sees the notice
// (route chunks are tens of KB), short enough that a genuinely stuck
// load gets named while the user is still looking at the screen.
const STALL_NOTICE_MS = 12_000;

// Reach location through globalThis so the node test pool (no DOM lib
// in the root tsconfig program) can import this module — same pattern
// as lazy-with-reload.ts. globalThis IS window in the browser.
const browser = globalThis as unknown as { location: { reload(): void } };

export function RouteLoadingFallback() {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setStalled(true), STALL_NOTICE_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div
      className="route-loading"
      role="status"
      aria-busy="true"
      aria-label="Loading page"
      data-testid="route-loading"
    >
      <Skeleton height={26} width="38%" />
      <Skeleton height={110} />
      <Skeleton height={110} />
      {stalled && (
        <div className="route-loading__stall" data-testid="route-loading-stall">
          <p className="route-loading__stall-text">
            This page is taking longer than expected. You can keep
            waiting, use the menu to go somewhere else, or reload.
          </p>
          <button
            type="button"
            className="route-loading__stall-button"
            onClick={() => browser.location.reload()}
          >
            Reload
          </button>
        </div>
      )}
    </div>
  );
}

// Plain-English copy for the two failure shapes. Exported for the unit
// test — the boundary itself needs a DOM to render.
export function routeErrorCopy(error: unknown): {
  title: string;
  detail: string;
} {
  if (isChunkLoadError(error)) {
    return {
      title: "This page needs a quick reload",
      detail:
        "Stewardship OS was updated while this tab was open. Reload to pick up the new version — you won't lose anything.",
    };
  }
  return {
    title: "Couldn't load this page",
    detail:
      "Something went wrong showing this page. The rest of the app still works — try again, or use the menu to go somewhere else.",
  };
}

interface RouteErrorBoundaryProps {
  // Changes on navigation (PageLayout passes location.pathname). When it
  // changes while an error is showing, the boundary clears itself so the
  // NEXT page gets a fresh start — without remounting children on every
  // path change (a key= would reset page state on /contacts/:id swaps).
  resetKey: string;
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: unknown | null;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  override state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown): void {
    // Same error-monitoring-only pipeline as the rest of the app (the
    // shared scrubber runs in beforeSend; no-op under vite dev).
    Sentry.captureException(error);
  }

  override componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    const copy = routeErrorCopy(this.state.error);
    return (
      <div className="route-error" role="alert" data-testid="route-error">
        <h2 className="route-error__title">{copy.title}</h2>
        <p className="route-error__detail">{copy.detail}</p>
        <div className="route-error__actions">
          <button
            type="button"
            className="route-error__button route-error__button--primary"
            onClick={() => browser.location.reload()}
          >
            Reload
          </button>
          <button
            type="button"
            className="route-error__button"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
